import { formatMoneyUz } from './formatMoneyUz';
import JsBarcode from 'jsbarcode';

type ReceiptPayment = {
  method: string;
  amount: number;
  breakdown?: { cash?: number; card?: number; click?: number; nasiya?: number; lines?: Array<{ label: string; amount: number }> };
};

export interface DesktopReceiptPayload {
  branchName?: string | null;
  cashierName?: string | null;
  cashierPhone?: string | null;
  orderId?: string | null;
  createdAtISO: string;
  items: Array<{ name: string; qty: number; price: number; total: number }>;
  total: number;
  subtotalBeforeDiscount?: number;
  discountAmount?: number;
  payment: ReceiptPayment;
  nasiyaClient?: { name: string; phone: string } | null;
  retailClient?: { name: string; phone: string } | null;
  contacts?: string | null;
  branchPhone?: string | null;
  tin?: string | null;
  legalName?: string | null;
  address?: string | null;
  saleNote?: string | null;
  workHours?: string | null;
  receiptStyle?: {
    order?: string[];
    enabled?: Record<string, boolean>;
    logoDataUrl?: string;
    blockTexts?: Partial<Record<'tin' | 'legal_name' | 'header_work_hours' | 'contacts', string>>;
  } | null;
}

type DesktopPrinterDevice = { name: string; displayName?: string; isDefault?: boolean } | null;
export type DesktopPrinterListItem = {
  name: string;
  displayName?: string;
  description?: string;
  isDefault?: boolean;
  options?: Record<string, unknown>;
};
export type DesktopOtherDeviceItem = { name: string; className?: string; status?: string; instanceId?: string };

type DesktopPrintersApi = {
  list: () => Promise<Array<DesktopPrinterListItem>>;
  listOtherDevices?: () => Promise<Array<DesktopOtherDeviceItem>>;
  autoSelect: (purpose: 'receipt' | 'barcode') => Promise<DesktopPrinterDevice>;
  printHtml: (payload: { purpose?: 'receipt' | 'barcode'; deviceName?: string; html: string; silent?: boolean; widthMm?: number; heightMm?: number }) => Promise<{ ok: boolean; printer?: string | null; error?: string }>;
};

function getDesktopPrintersApi(): DesktopPrintersApi | null {
  const api = (window as any)?.addeDesktop?.printers;
  if (!api || typeof api.list !== 'function' || typeof api.autoSelect !== 'function' || typeof api.printHtml !== 'function') {
    return null;
  }
  return api as DesktopPrintersApi;
}

export function isDesktopPrinterApiAvailable(): boolean {
  return Boolean(getDesktopPrintersApi());
}

/** Adde Desktop (Electron) ilovasida ishlayotganini aniqlash. */
export function isAddeDesktopRuntime(): boolean {
  if (typeof navigator !== 'undefined' && /addedesktop/i.test(String(navigator.userAgent || ''))) {
    return true;
  }
  if (typeof window !== 'undefined' && (window as { addeDesktop?: unknown }).addeDesktop) {
    return true;
  }
  return false;
}

/** Desktop chek chop etishdan oldin printer API va keshni tayyorlash. */
export async function ensureDesktopPrintReady(): Promise<{ ready: boolean; error?: string }> {
  if (!isDesktopPrinterApiAvailable()) {
    return {
      ready: false,
      error: 'Desktop printer API topilmadi. Ilovani yoping va qayta oching.',
    };
  }
  try {
    await refreshDesktopPrinterCache();
    return { ready: true };
  } catch (e: unknown) {
    return {
      ready: false,
      error: e instanceof Error ? e.message : 'Printer tayyorligini tekshirib bo\'lmadi.',
    };
  }
}

const RECEIPT_PREF_KEY = 'adde_desktop_printer_receipt_v1';
const BARCODE_PREF_KEY = 'adde_desktop_printer_barcode_v1';
const PRINTER_EVENT = 'adde-desktop-printers-changed';
let cachedPrinters: DesktopPrinterListItem[] = [];
let cachedOtherDevices: DesktopOtherDeviceItem[] = [];
let cachedStatus: DesktopPrinterStatus | null = null;
let monitorTimer: number | null = null;

function emitPrinterCache() {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent(PRINTER_EVENT, {
      detail: {
        status: cachedStatus,
        printers: cachedPrinters,
        otherDevices: cachedOtherDevices,
      },
    }));
  } catch {}
}

export function getDesktopPrinterEventName(): string {
  return PRINTER_EVENT;
}

export function getCachedDesktopPrinterStatus(): DesktopPrinterStatus | null {
  return cachedStatus;
}

export function getCachedDesktopPrinters(): DesktopPrinterListItem[] {
  return cachedPrinters;
}

export function getCachedDesktopOtherDevices(): DesktopOtherDeviceItem[] {
  return cachedOtherDevices;
}

function chooseFallbackPrinter(
  list: DesktopPrinterListItem[],
  purpose: 'receipt' | 'barcode',
  receiptPrinterName?: string | null
): DesktopPrinterListItem | null {
  if (!Array.isArray(list) || list.length === 0) return null;
  if (purpose === 'receipt') {
    return list.find((p) => Boolean(p?.isDefault)) || list[0] || null;
  }
  const receiptNorm = String(receiptPrinterName || '').trim().toLowerCase();
  const nonReceipt = list.filter((p) => {
    const name = String(p?.name || '').trim().toLowerCase();
    const displayName = String(p?.displayName || '').trim().toLowerCase();
    if (receiptNorm && (name === receiptNorm || displayName === receiptNorm)) return false;
    return !isLikelyReceiptPrinterName(name) && !isLikelyReceiptPrinterName(displayName);
  });
  if (nonReceipt.length > 0) return nonReceipt[0];
  // Some setups use a single thermal printer for both receipt and barcode.
  if (list.length === 1) return list[0];
  return null;
}

function derivePrinterStatus(list: DesktopPrinterListItem[]): DesktopPrinterStatus {
  const preferredReceipt = getPreferredPrinterName('receipt');
  const preferredBarcode = getPreferredPrinterName('barcode');
  const preferredReceiptObj = resolvePreferredPrinter(list, preferredReceipt);
  const preferredBarcodeObj = resolvePreferredPrinter(list, preferredBarcode);
  const fallbackReceipt = chooseFallbackPrinter(list, 'receipt', null);
  const receiptPrinterName =
    preferredReceiptObj?.displayName ||
    preferredReceiptObj?.name ||
    fallbackReceipt?.displayName ||
    fallbackReceipt?.name ||
    null;
  const fallbackBarcode = chooseFallbackPrinter(list, 'barcode', receiptPrinterName);
  const barcodePrinterName =
    preferredBarcodeObj?.displayName ||
    preferredBarcodeObj?.name ||
    (isSamePrinter(fallbackBarcode?.name, receiptPrinterName) || isSamePrinter(fallbackBarcode?.displayName, receiptPrinterName)
      ? null
      : fallbackBarcode?.displayName || fallbackBarcode?.name || null);
  return {
    available: true,
    printersCount: list.length,
    receiptPrinterName,
    barcodePrinterName,
  };
}

export async function refreshDesktopPrinterCache(options?: { includeOtherDevices?: boolean }): Promise<DesktopPrinterStatus> {
  if (!isDesktopPrinterApiAvailable()) {
    cachedStatus = { available: false, printersCount: 0, receiptPrinterName: null, barcodePrinterName: null };
    cachedPrinters = [];
    cachedOtherDevices = [];
    emitPrinterCache();
    return cachedStatus;
  }
  const printers = await listDesktopPrinters().catch(() => []);
  const basePrinters = Array.isArray(printers) ? printers : [];
  if (options?.includeOtherDevices) {
    const otherDevices = await listDesktopOtherDevices().catch(() => []);
    cachedOtherDevices = Array.isArray(otherDevices) ? otherDevices : [];
  } else {
    cachedOtherDevices = [];
  }
  cachedPrinters = basePrinters;
  cachedStatus = derivePrinterStatus(cachedPrinters);
  emitPrinterCache();
  return cachedStatus;
}

export function startDesktopPrinterMonitor(intervalMs = 10000): void {
  if (typeof window === 'undefined') return;
  if (!isDesktopPrinterApiAvailable()) return;
  if (monitorTimer != null) return;
  void refreshDesktopPrinterCache().catch(() => {});
  monitorTimer = window.setInterval(() => {
    void refreshDesktopPrinterCache().catch(() => {});
  }, intervalMs);
}

function getPreferredPrinterName(purpose: 'receipt' | 'barcode'): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const key = purpose === 'barcode' ? BARCODE_PREF_KEY : RECEIPT_PREF_KEY;
    const v = window.localStorage?.getItem?.(key);
    const trimmed = String(v || '').trim();
    return trimmed || null;
  } catch {
    return null;
  }
}

export function getPreferredDesktopPrinter(purpose: 'receipt' | 'barcode'): string | null {
  return getPreferredPrinterName(purpose);
}

export function setPreferredDesktopPrinter(purpose: 'receipt' | 'barcode', printerName: string | null) {
  if (typeof window === 'undefined') return;
  const key = purpose === 'barcode' ? BARCODE_PREF_KEY : RECEIPT_PREF_KEY;
  try {
    if (printerName && String(printerName).trim()) {
      window.localStorage?.setItem?.(key, String(printerName).trim());
    } else {
      window.localStorage?.removeItem?.(key);
    }
  } catch {
    // ignore storage errors
  }
  void refreshDesktopPrinterCache().catch(() => {});
}

function isSamePrinter(a: string | null | undefined, b: string | null | undefined): boolean {
  const aa = String(a || '').trim().toLowerCase();
  const bb = String(b || '').trim().toLowerCase();
  return !!aa && !!bb && aa === bb;
}

function resolvePreferredPrinter(list: DesktopPrinterListItem[], preferred: string | null): DesktopPrinterListItem | null {
  const raw = String(preferred || '').trim();
  if (!raw) return null;
  const value = raw.toLowerCase();
  const compact = value.replace(/[^a-z0-9]+/g, '');
  for (const printer of list) {
    const name = String(printer?.name || '').trim().toLowerCase();
    const displayName = String(printer?.displayName || '').trim().toLowerCase();
    if (value && (name === value || displayName === value)) return printer;
    const nameCompact = name.replace(/[^a-z0-9]+/g, '');
    const displayCompact = displayName.replace(/[^a-z0-9]+/g, '');
    if (compact && (nameCompact === compact || displayCompact === compact)) return printer;
  }
  return null;
}

function isVirtualPrinterName(name: string): boolean {
  const n = String(name || '').toLowerCase();
  return n.includes('pdf') || n.includes('xps') || n.includes('onenote') || n.includes('fax');
}

function isLikelyReceiptPrinterName(name: string): boolean {
  const n = String(name || '').toLowerCase();
  return /(receipt|kassa|chek|pos|thermal|epson|80mm|printer-80|printer 80|xp-80|xp80)/.test(n);
}

function isFilePortPrinter(item: DesktopPrinterListItem): boolean {
  const port = String(item?.options?.portName ?? item?.options?.['port-name'] ?? '').trim().toLowerCase();
  if (!port) return false;
  if (port === 'file' || port.startsWith('file:')) return true;
  if (port.includes('portprompt')) return true;
  if (port.startsWith('nul')) return true;
  return false;
}

function inferPrintersFromDeviceList(
  devices: DesktopOtherDeviceItem[],
  existing: DesktopPrinterListItem[]
): DesktopPrinterListItem[] {
  const existingNames = new Set(
    existing
      .map((p) => String(p?.name || '').trim().toLowerCase())
      .filter(Boolean)
  );
  return devices
    .filter((d) => {
      const name = String(d?.name || '').trim();
      if (!name) return false;
      const low = name.toLowerCase();
      if (existingNames.has(low)) return false;
      if (/(intel|chipset|hidclass|hid|controller|host|audio|bluetooth|camera|usb root hub)/.test(low)) return false;
      return /(xprinter|xp-|zebra|tsc|label|barcode|printer)/.test(low);
    })
    .map((d) => ({
      name: String(d.name || '').trim(),
      displayName: String(d.name || '').trim(),
      description: 'Detected printer device',
      isDefault: false,
      options: {
        inferred: true,
        className: d.className || '',
        status: d.status || '',
        instanceId: d.instanceId || '',
      },
    }));
}

export async function listDesktopPrinters(): Promise<DesktopPrinterListItem[]> {
  const api = getDesktopPrintersApi();
  if (!api) return [];
  const [list, otherDevices] = await Promise.all([
    api.list().catch(() => []),
    typeof api.listOtherDevices === 'function' ? api.listOtherDevices().catch(() => []) : Promise.resolve([]),
  ]);
  const normalized = Array.isArray(list) ? list : [];
  const filtered = normalized.filter(
    (p) => !isVirtualPrinterName(p?.name || '') && !isVirtualPrinterName(p?.displayName || '') && !isFilePortPrinter(p),
  );
  const inferred = inferPrintersFromDeviceList(Array.isArray(otherDevices) ? otherDevices : [], filtered);
  return [...filtered, ...inferred];
}

export async function listDesktopOtherDevices(): Promise<DesktopOtherDeviceItem[]> {
  const api = getDesktopPrintersApi();
  if (!api || typeof api.listOtherDevices !== 'function') return [];
  const list = await api.listOtherDevices().catch(() => []);
  return Array.isArray(list) ? list : [];
}

function esc(s: string) {
  return String(s ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

function escAttr(s: string) {
  return String(s ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('"', '&quot;')
    .replaceAll('<', '&lt;');
}

function formatDateTime(iso: string) {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** Haqiqiy brand logotipini PNG dan base64 sifatida yuklash */
async function fetchBrandLogoDataUrl(): Promise<string> {
  const candidates = [
    '/adde-logo-light-trim.png',
    '/adde-logo.png',
  ];
  for (const path of candidates) {
    try {
      const resp = await fetch(path);
      if (!resp.ok) continue;
      const blob = await resp.blob();
      const dataUrl = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(typeof reader.result === 'string' ? reader.result : '');
        reader.onerror = () => resolve('');
        reader.readAsDataURL(blob);
      });
      if (dataUrl) return dataUrl;
    } catch {
      // keyingi variantni sinab ko'r
    }
  }
  return '';
}

function paymentLines(payment: ReceiptPayment) {
  const PAY: Record<string, string> = { cash: 'Naqd', card: 'Karta', click: 'Click', nasiya: 'Nasiya', mixed: 'Aralash' };
  if (payment.method === 'mixed' && payment.breakdown) {
    if (Array.isArray(payment.breakdown.lines) && payment.breakdown.lines.length > 0) {
      return payment.breakdown.lines
        .filter((x) => (Number(x.amount) || 0) > 0)
        .map((x) => `${esc(x.label)}: ${formatMoneyUz(Number(x.amount) || 0)}`)
        .join('<br/>');
    }
    const rows = ['cash', 'card', 'click', 'nasiya']
      .filter((k) => Number((payment.breakdown as any)[k]) > 0)
      .map((k) => `${PAY[k] || k}: ${formatMoneyUz(Number((payment.breakdown as any)[k]) || 0)}`);
    return rows.join('<br/>');
  }
  return `${PAY[payment.method] || payment.method}: ${formatMoneyUz(payment.amount)}`;
}

function buildReceiptHtml(data: DesktopReceiptPayload, fallbackLogoDataUrl?: string): string {
  const itemsHtml = data.items
    .map((it, idx) => `<tr><td class="name">${idx + 1}. ${esc(it.name)}</td><td class="qty">${Number(it.qty || 0)} x ${esc(formatMoneyUz(it.price))}</td></tr><tr><td></td><td class="sum">${esc(formatMoneyUz(it.total))}</td></tr>`)
    .join('');
  const enabled = data.receiptStyle?.enabled || {};
  const order = Array.isArray(data.receiptStyle?.order) ? data.receiptStyle?.order || [] : [];
  const blockTexts = data.receiptStyle?.blockTexts || {};
  const on = (id: string) => enabled[id] !== false;
  const row = (label: string, value: string | null | undefined) => `<div class="row"><span>${esc(label)}</span><b>${esc(value || '—')}</b></div>`;
  const discountAmt = Math.max(0, Math.round(Number(data.discountAmount) || 0));
  const subtotal =
    Number(data.subtotalBeforeDiscount) > 0
      ? Math.round(Number(data.subtotalBeforeDiscount))
      : discountAmt > 0
        ? Math.round(Number(data.total) + discountAmt)
        : Math.round(Number(data.total));
  const retailLine = (() => {
    const rc = data.retailClient;
    const nas = data.nasiyaClient;
    const name = rc?.name?.trim() || nas?.name?.trim() || '';
    const phone = rc?.phone?.trim() || nas?.phone?.trim() || '';
    if (!name && !phone) return '—';
    if (name && phone) return `${name} (${phone})`;
    return name || phone;
  })();
  const totalsBlock = (() => {
    const parts: string[] = [];
    if (on('receipt_totals') && discountAmt > 0) {
      parts.push(row('Oraliq jami', formatMoneyUz(subtotal)));
    }
    if (on('receipt_discounts') && discountAmt > 0) {
      parts.push(row('Chegirma', `-${formatMoneyUz(discountAmt)}`));
    }
    if (on('receipt_totals')) {
      parts.push(`<div class="grand"><span>JAMI</span><span>${esc(formatMoneyUz(data.total))}</span></div>`);
    }
    return parts.join('');
  })();
  const blocks: Record<string, string> = {
    header_sale_number: data.orderId ? `<div class="sale">Sotuv #${esc(String(data.orderId))}</div>` : '',
    header_store: `<div class="store">Do'kon: ${esc(data.branchName || data.legalName || 'Filial')}</div>`,
    header_datetime: `<div>Sana: ${esc(formatDateTime(data.createdAtISO))}</div>`,
    header_work_hours: `<div>Ish vaqti: ${esc(blockTexts.header_work_hours || data.workHours || '—')}</div>`,
    cashier: row('Kassir', data.cashierName || 'Kassir'),
    client: row('Chakana mijoz', retailLine),
    contacts: row(
      'Kontaktlar',
      blockTexts.contacts || data.contacts || data.branchPhone || data.cashierPhone || '—',
    ),
    tin: row('STIR', blockTexts.tin || data.tin || '—'),
    legal_name: row('Yuridik nom', blockTexts.legal_name || data.legalName || '—'),
    address: row('Manzil', data.address || '—'),
    client_phone: '',
    sale_note: data.saleNote ? `<div>Izoh: ${esc(data.saleNote)}</div>` : '',
    line_items: `<div class="sep"></div><table class="t">${itemsHtml}</table>`,
    receipt_totals: totalsBlock,
    receipt_discounts: '',
    line_totals: '',
    line_discounts: '',
    product_count: on('product_count') ? row('Mahsulotlar soni', String(data.items.length)) : '',
  };
  const defaultOrder = ['header_store', 'header_datetime', 'header_sale_number', 'cashier', 'client', 'line_items', 'receipt_totals', 'product_count'];
  const resolvedOrder = [...order.filter((x) => blocks[x] !== undefined), ...defaultOrder.filter((x) => !order.includes(x))];
  const content = resolvedOrder
    .filter((id) => on(id))
    .map((id) => blocks[id])
    .filter(Boolean)
    .join('');
  const logoSrcRaw = String(data.receiptStyle?.logoDataUrl || fallbackLogoDataUrl || '').trim();
  const hasValidLogo = /^data:image\/(png|jpe?g|gif|webp|svg\+xml);/i.test(logoSrcRaw);
  const logoHtml = hasValidLogo
    ? `<div class="logo-wrap"><img class="logo" src="${escAttr(logoSrcRaw)}" alt="Adde"/></div>`
    : `<div class="logo-wrap logo-wrap--default"><svg xmlns="http://www.w3.org/2000/svg" viewBox="8 18 384 96" fill="none" style="-webkit-print-color-adjust:exact;print-color-adjust:exact"><circle cx="52" cy="86" r="26" fill="#777"/><text x="104" y="90" fill="#000" font-family="Arial Black,Arial,sans-serif" font-size="68" font-weight="900" letter-spacing="-0.03em">adde</text></svg></div>`;
  return `<!doctype html><html><head><meta charset="utf-8"/><style>
@page{size:80mm auto;margin:0}
html,body{margin:0;padding:0}
body{box-sizing:border-box;width:72mm;font-family:"Segoe UI",Arial,sans-serif;font-size:11px;line-height:1.26;font-weight:600;padding:1.2mm 0.6mm 1.2mm 1.2mm;color:#111}
.logo-wrap{display:flex;justify-content:center;align-items:center;margin:1mm 0 2.5mm;width:100%}
.logo-wrap--default svg{display:block;width:100%;max-width:52mm;height:auto;max-height:12mm;margin:0 auto}
.logo{display:block;max-width:56mm;max-height:14mm;width:auto;height:auto;object-fit:contain;-webkit-print-color-adjust:exact;print-color-adjust:exact}
.sale{font-weight:800;margin-bottom:6px}
.store{text-align:center;font-size:13.5px;font-weight:800;margin:3px 0 9px;white-space:normal;overflow-wrap:anywhere}
.row{display:flex;justify-content:space-between;align-items:flex-start;gap:8px;margin:4px 0}
.row span{flex:1 1 48%;min-width:0}
.row b{flex:1 1 52%;font-weight:650;text-align:left;white-space:normal;overflow-wrap:anywhere;word-break:break-word}
.sep{border-top:1px dashed #9ca3af;margin:8px 0}
.t{width:100%;border-collapse:collapse;table-layout:fixed}
.name{width:64%;vertical-align:top;white-space:normal;overflow-wrap:anywhere;word-break:break-word;padding:1px 0}
.qty,.sum{text-align:left;vertical-align:top;width:36%;white-space:normal;overflow-wrap:anywhere;word-break:break-word}
.sum{padding-bottom:5px;font-weight:700}
.pay{margin-top:8px;white-space:normal;overflow-wrap:anywhere}
.grand{display:flex;justify-content:space-between;gap:8px;border-top:2px solid #111;margin-top:8px;padding-top:5px;font-weight:900;font-size:12px}
.thanks{text-align:center;margin-top:12px;font-weight:800}
</style></head><body>
${logoHtml}
${content}
<div class="sep"></div>
<div class="pay"><b>To'lov:</b><br/>${paymentLines(data.payment)}</div>
${!on('receipt_totals') ? `<div class="grand"><span>JAMI</span><span>${esc(formatMoneyUz(data.total))}</span></div>` : ''}
<div class="thanks">Xaridingiz uchun rahmat!</div>
</body></html>`;
}

export async function printReceiptViaDesktop(payload: DesktopReceiptPayload): Promise<{ ok: boolean; error?: string; printer?: string | null }> {
  const api = getDesktopPrintersApi();
  if (!api) return { ok: false, error: 'Desktop printer API topilmadi' };
  const preferredName = getPreferredPrinterName('receipt');
  const safePreferred =
    preferredName && !isVirtualPrinterName(preferredName) && !preferredName.toLowerCase().includes('file')
      ? preferredName
      : null;
  const selected = safePreferred ? null : await api.autoSelect('receipt');
  const fallbackLogo = await fetchBrandLogoDataUrl();
  const html = buildReceiptHtml(payload, fallbackLogo);
  const res = await api.printHtml({
    purpose: 'receipt',
    deviceName: safePreferred || selected?.name || undefined,
    html,
    silent: true,
    widthMm: 80,
  });
  return res?.ok
    ? { ok: true, printer: res.printer || safePreferred || selected?.displayName || selected?.name || null }
    : { ok: false, printer: res?.printer || safePreferred || selected?.name || null, error: res?.error || 'Desktop chop etishda xatolik' };
}

export interface DesktopPrinterStatus {
  available: boolean;
  printersCount: number;
  receiptPrinterName: string | null;
  barcodePrinterName: string | null;
}

export async function getDesktopPrinterStatus(): Promise<DesktopPrinterStatus> {
  const api = getDesktopPrintersApi();
  if (!api) {
    return { available: false, printersCount: 0, receiptPrinterName: null, barcodePrinterName: null };
  }
  const [list, receipt, barcode] = await Promise.all([
    api.list().catch(() => []),
    api.autoSelect('receipt').catch(() => null),
    api.autoSelect('barcode').catch(() => null),
  ]);
  const normalizedList = Array.isArray(list) ? list : [];
  const preferredReceipt = getPreferredPrinterName('receipt');
  const preferredBarcode = getPreferredPrinterName('barcode');
  const preferredReceiptObj = resolvePreferredPrinter(normalizedList, preferredReceipt);
  const preferredBarcodeObj = resolvePreferredPrinter(normalizedList, preferredBarcode);
  return {
    available: true,
    printersCount: normalizedList.length,
    receiptPrinterName: preferredReceiptObj?.displayName || preferredReceiptObj?.name || receipt?.displayName || receipt?.name || null,
    barcodePrinterName:
      preferredBarcodeObj?.displayName ||
      preferredBarcodeObj?.name ||
      (isSamePrinter(barcode?.name, preferredReceipt) || isSamePrinter(barcode?.displayName, preferredReceipt)
        ? null
        : barcode?.displayName || barcode?.name || null),
  };
}

function toSvgBarcodeData(barcode: string): string {
  const value = String(barcode || '').trim();
  const svgEl = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  JsBarcode(svgEl, value, {
    format: 'CODE128',
    displayValue: false,
    width: 1.2,
    height: 36,
    margin: 0,
    fontSize: 11,
  });
  const serialized = new XMLSerializer().serializeToString(svgEl);
  return `data:image/svg+xml;utf8,${encodeURIComponent(serialized)}`;
}

export interface DesktopBarcodePrintPayload {
  barcode: string;
  name?: string;
  copies: number;
  widthMm: number;
  heightMm: number;
}

function buildBarcodeSheetHtml(payload: DesktopBarcodePrintPayload, safeCopies: number, widthMm: number, heightMm: number): string {
  const padMm = Math.max(1.5, heightMm * 0.045);
  const numFontPt = Math.max(7, Math.min(9, heightMm * 0.2));
  const numHeightMm = numFontPt * 0.353 * 1.6;
  const gapMm = 1;
  const barcodeHeightMm = Math.max(8, heightMm - padMm * 2 - numHeightMm - gapMm);
  // Faqat barcode rasmi + raqam: mahsulot nomi ko'rsatilmaydi
  const labelHtml = `<div class="lbl"><div class="bc"><img src="${toSvgBarcodeData(payload.barcode)}" alt="barcode"/></div><div class="num">${esc(payload.barcode)}</div></div>`;
  return `<!doctype html><html><head><meta charset="utf-8"/><style>
  @page{size:${widthMm}mm ${heightMm}mm;margin:0}
  html,body{margin:0;padding:0;width:${widthMm}mm}
  body{font-family:Arial,sans-serif;-webkit-print-color-adjust:exact;print-color-adjust:exact}
  .sheet{display:block}
  .lbl{
    width:${widthMm}mm;
    height:${heightMm}mm;
    box-sizing:border-box;
    padding:${padMm}mm ${padMm + 0.5}mm;
    display:flex;
    flex-direction:column;
    justify-content:center;
    align-items:center;
    gap:${gapMm}mm;
    break-inside:avoid;
    page-break-inside:avoid;
  }
  .bc{
    display:flex;
    justify-content:center;
    align-items:center;
    width:100%;
    flex-shrink:0;
  }
  img{
    display:block;
    width:auto;
    max-width:96%;
    height:${barcodeHeightMm}mm;
    max-height:${barcodeHeightMm}mm;
    object-fit:contain;
  }
  .num{
    font-size:${numFontPt}pt;
    line-height:1.1;
    letter-spacing:0.08em;
    font-weight:700;
    color:#000;
    text-align:center;
    white-space:nowrap;
    flex-shrink:0;
  }
  </style></head><body><div class="sheet">${Array.from({ length: safeCopies }).map(() => labelHtml).join('')}</div></body></html>`;
}

export async function printBarcodeViaBrowser(payload: DesktopBarcodePrintPayload): Promise<{ ok: boolean; error?: string }> {
  try {
    const safeCopies = Math.max(1, Math.min(200, Math.round(Number(payload.copies) || 1)));
    const widthMm = Math.max(20, Math.min(120, Number(payload.widthMm) || 58));
    const heightMm = Math.max(20, Math.min(120, Number(payload.heightMm) || 25));
    const html = buildBarcodeSheetHtml(payload, safeCopies, widthMm, heightMm);
    const w = window.open('', '_blank', 'noopener,width=480,height=720,scrollbars=yes');
    if (!w) return { ok: false, error: 'Brauzer popup bloklandi' };
    w.document.open();
    w.document.write(html);
    w.document.close();
    window.setTimeout(() => {
      try {
        w.focus();
        w.print();
      } catch {
        // ignore popup print failures
      }
    }, 120);
    return { ok: true };
  } catch (e: any) {
    return { ok: false, error: String(e?.message || 'Shtrix-kod chop etib bo\'lmadi') };
  }
}

export async function printBarcodeViaDesktop(payload: DesktopBarcodePrintPayload): Promise<{ ok: boolean; error?: string }> {
  const api = getDesktopPrintersApi();
  if (!api) return { ok: false, error: 'Desktop printer API topilmadi' };
  const printers = await listDesktopPrinters().catch(() => []);
  const preferredName = getPreferredPrinterName('barcode');
  const receiptName = getPreferredPrinterName('receipt');
  const preferredPrinter = resolvePreferredPrinter(printers, preferredName);
  const hasAlternativeNonReceipt = printers.some((p) => {
    const name = String(p?.name || '').trim().toLowerCase();
    const displayName = String(p?.displayName || '').trim().toLowerCase();
    if (isLikelyReceiptPrinterName(name) || isLikelyReceiptPrinterName(displayName)) return false;
    return true;
  });
  const safePreferred =
    preferredPrinter?.name ||
    (preferredName && !isVirtualPrinterName(preferredName) && !preferredName.toLowerCase().includes('file')
      ? preferredName
      : null);
  if (!safePreferred) {
    return { ok: false, error: 'Shtrix-kod printeri tanlanmagan. Printer sozlamalaridan label/shtrix-kod printerini tanlang.' };
  }
  if (
    hasAlternativeNonReceipt &&
    (
      isLikelyReceiptPrinterName(preferredPrinter?.name || '') ||
      isLikelyReceiptPrinterName(preferredPrinter?.displayName || '') ||
      isLikelyReceiptPrinterName(safePreferred)
    )
  ) {
    return { ok: false, error: 'Shtrix-kod uchun 80mm chek printer tanlangan. Label/shtrix-kod printerni tanlang.' };
  }
  if (isSamePrinter(safePreferred, receiptName)) {
    return { ok: false, error: 'Shtrix-kod printeri chek printeri bilan bir xil tanlangan. Alohida shtrix-kod/label printerini tanlang.' };
  }
  const safeCopies = Math.max(1, Math.min(500, Math.round(Number(payload.copies) || 1)));
  const widthMm = Math.max(20, Math.min(120, Number(payload.widthMm) || 58));
  const heightMm = Math.max(20, Math.min(120, Number(payload.heightMm) || 25));
  const html = buildBarcodeSheetHtml(payload, safeCopies, widthMm, heightMm);
  const res = await api.printHtml({
    purpose: 'barcode',
    deviceName: safePreferred,
    html,
    silent: true,
    widthMm,
    heightMm,
  });
  return res?.ok ? { ok: true } : { ok: false, error: res?.error || 'Shtrix-kod chop etishda xatolik' };
}
